import React from "react";
import { Tooltip } from "@mui/material";

export const TOKENS = [
  { key: "fan_full_name", label: "Fan's Full Name" },
  { key: "fan_first_name", label: "Fan's First Name" },
  { key: "band_name", label: "Band's Name" },
  { key: "artist_first_name", label: "Artist's First Name" },
  { key: "artist_full_name", label: "Artist's Full Name" },
  { key: "link", label: "Link" }
];

export const PLACEHOLDER_TOKENS = [
  "site url",
  "venue name",
  "date",
  "fan name",
  "song name",
  "website url",
  "band name",
  "store name",
  "show name"
];

export const TOKEN_REGEX = /\[([a-zA-Z0-9_ ]+|https?:\/\/[^\]\s]+)\]|https?:\/\/[^\s]+/g;
export const URL_REGEX = /^https?:\/\/.+/i;

export type ValidationError = {
  type: 'standalone-url' | 'placeholder' | 'invalid';
  token: string;
  message: string;
  tooltip?: string;
};

export function validateMessage(messageText: string): ValidationError[] {
  const errors: ValidationError[] = [];
  const regex = new RegExp(TOKEN_REGEX);
  let match: RegExpExecArray | null;

  while ((match = regex.exec(messageText)) !== null) {
    const [fullMatch, tokenContent] = match;

    if (!tokenContent && URL_REGEX.test(fullMatch)) {
      errors.push({
        type: 'standalone-url',
        token: fullMatch,
        message: 'Please add brackets around URL to enable tracking functionality.',
        tooltip: 'URLs wrapped in brackets are automatically shortened and tracked for analytics.'
      });
      continue;
    }

    if (tokenContent && PLACEHOLDER_TOKENS.includes(tokenContent.toLowerCase())) {
      errors.push({
        type: 'placeholder',
        token: `[${tokenContent}]`,
        message: 'Please replace with actual value.'
      });
      continue;
    }

    if (tokenContent && !URL_REGEX.test(tokenContent)) {
      const isValidToken = TOKENS.some(t => t.key === tokenContent);
      if (!isValidToken) {
        errors.push({
          type: 'invalid',
          token: `[${tokenContent}]`,
          message: 'Invalid token. Use Add Token button to insert a valid token.',
          tooltip: 'Only predefined tokens are supported for dynamic content replacement.'
        });
      }
    }
  }

  return errors;
}

const tokenStyles: Record<string, React.CSSProperties> = {
  valid: { background: "#06b2da", color: "#fff" },
  url: { background: "#07C8B8", color: "#fff" },
  placeholder: { background: "#ff9800", color: "#fff" },
  standaloneUrl: { background: "#ffeb3b", color: "#000" }
};

function getTooltipTitle(
  isValid: boolean,
  label: string,
  isUrl: boolean,
  isPlaceholder: boolean,
  isStandaloneUrl = false
) {
  if (isStandaloneUrl) return "Wrap this URL in brackets [url] to ensure tracking and analytics";
  if (isUrl) return "This link will be replaced with shorter custom URL per fan to ensure tracking and analytics";
  if (isPlaceholder) return "Replace this placeholder with an actual value via Add Token button";
  return isValid
    ? `It will be replaced with ${label.toLowerCase()}`
    : "Invalid token, use Add Token button to insert a valid token";
}

function getTokenStyle(
  isValid: boolean,
  isUrl: boolean,
  isPlaceholder: boolean,
  isStandaloneUrl = false
) {
  if (isStandaloneUrl) return tokenStyles.standaloneUrl;
  if (isUrl) return tokenStyles.url;
  if (isValid) return tokenStyles.valid;
  if (isPlaceholder) return tokenStyles.placeholder;
  return {};
}

function renderTokenTooltip(
  tokenKey: string,
  label: string,
  isValid: boolean,
  isUrl = false,
  isPlaceholder = false,
  isStandaloneUrl = false
) {
  const title = getTooltipTitle(isValid, label, isUrl, isPlaceholder, isStandaloneUrl);
  const style = { ...styles.tokenToolTip, ...getTokenStyle(isValid, isUrl, isPlaceholder, isStandaloneUrl) };

  return (
    <Tooltip title={title} key={tokenKey + label + String(isValid) + String(isUrl) + String(isPlaceholder) + String(isStandaloneUrl)}>
      <span style={style}>{isStandaloneUrl ? tokenKey : `[${tokenKey}]`}</span>
    </Tooltip>
  );
}

export function renderWithTokenHighlight(text: string) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = new RegExp(TOKEN_REGEX);
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const [fullMatch, tokenContent] = match;

    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const isStandaloneUrl = !tokenContent && URL_REGEX.test(fullMatch);

    if (isStandaloneUrl) {
      parts.push(renderTokenTooltip(fullMatch, "", false, false, false, true));
    } else {
      const isUrl = tokenContent ? URL_REGEX.test(tokenContent) : false;
      const tokenDef = tokenContent ? TOKENS.find(t => t.key === tokenContent) : undefined;
      const isPlaceholder = tokenContent ? PLACEHOLDER_TOKENS.includes(tokenContent.toLowerCase()) : false;

      parts.push(
        renderTokenTooltip(
          tokenContent ?? "",
          tokenDef?.label || "",
          Boolean(tokenDef),
          isUrl,
          isPlaceholder,
          false
        )
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

const styles = {
  tokenToolTip: {
    background: "#ccc",
    borderRadius: "6px",
    padding: "1px",
    fontFamily: "Montserrat",
    fontSize: "0.9375rem",
    fontWeight: 500,
    cursor: "pointer"
  }
};
